import { useEffect, useMemo, useState } from "react";
import { Package, DollarSign, Users, Boxes, Mail, Trash2, Plus, Check, BadgeDollarSign, MapPin, ChevronDown, Copy, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { baseName } from "@/lib/group";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TABS = ["Overview", "Orders", "Emails", "Products", "Messages", "Affiliates"];

export default function Admin() {
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [affStats, setAffStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [rates, setRates] = useState({});
  const [openOrder, setOpenOrder] = useState(null);
  const [tracking, setTracking] = useState({});
  const [pricing, setPricing] = useState({});
  const [range, setRange] = useState(14);

  const paidOrders = useMemo(() => orders.filter((o) => o.status === "paid"), [orders]);

  const inRange = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - (range - 1)); cutoff.setHours(0, 0, 0, 0);
    return paidOrders.filter((o) => new Date(o.paid_at || o.created_at || 0) >= cutoff);
  }, [paidOrders, range]);

  const revenueSeries = useMemo(() => {
    const days = [];
    const now = new Date();
    const step = range > 30 ? Math.ceil(range / 15) : 1;
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(now); d.setDate(now.getDate() - i);
      days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }), revenue: 0 });
    }
    const idx = Object.fromEntries(days.map((d, i) => [d.key, i]));
    for (const o of inRange) {
      const k = (o.paid_at || o.created_at || "").slice(0, 10);
      if (k in idx) days[idx[k]].revenue += Number(o.total || 0);
    }
    return days.map((d, i) => ({ ...d, revenue: Math.round(d.revenue * 100) / 100, tick: i % step === 0 }));
  }, [inRange, range]);

  const topSellers = useMemo(() => {
    const tally = {};
    for (const o of inRange) for (const it of (o.items || [])) {
      const name = baseName(it.name || it.slug || "");
      tally[name] = (tally[name] || 0) + Number(it.qty || 1);
    }
    return Object.entries(tally).map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty).slice(0, 6);
  }, [inRange]);

  const saveTracking = async (order_num) => {
    try {
      await api.put(`/admin/orders/${order_num}/tracking`, { tracking_number: tracking[order_num] ?? "" });
      toast.success("Tracking saved — customer can now see it");
      setOrders((prev) => prev.map((o) => o.order_num === order_num ? { ...o, tracking_number: tracking[order_num] ?? "" } : o));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const savePricing = async (p) => {
    const edit = pricing[p.slug] || {};
    const price = parseFloat(edit.price ?? p.price);
    const in_stock = edit.in_stock ?? p.in_stock ?? true;
    if (isNaN(price) || price < 0) return toast.error("Enter a valid price");
    try {
      const { data } = await api.put(`/admin/products/${p.slug}/pricing`, { price, in_stock });
      toast.success(data.stripe_synced ? "Saved & synced to Stripe" : "Saved (Stripe sync pending)");
      setProducts((prev) => prev.map((x) => x.slug === p.slug ? { ...x, price, in_stock } : x));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetTestData = async () => {
    try {
      const { data } = await api.post("/admin/reset-test-data");
      toast.success(`Cleared ${data.orders_deleted} orders & ${data.customers_deleted} customers`);
      loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/admin/messages").then(({ data }) => setMessages(data)).catch(() => {});
    api.get("/admin/affiliates").then(({ data }) => setAffiliates(data)).catch(() => {});
    api.get("/admin/affiliate-stats").then(({ data }) => setAffStats(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
  };
  useEffect(loadAll, []);

  const copyEmails = () => {
    if (leads.length === 0) return toast.error("No emails to copy");
    navigator.clipboard.writeText(leads.map((l) => l.email).join(", "));
    toast.success(`Copied ${leads.length} email${leads.length === 1 ? "" : "s"}`);
  };
  const exportCsv = () => {
    if (leads.length === 0) return toast.error("No emails to export");
    const rows = [["email", "signup_date"], ...leads.map((l) => [l.email, l.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `ape-tides-emails-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const delProduct = async (slug) => {
    try { await api.delete(`/admin/products/${slug}`); toast.success("Product deleted"); setProducts((p) => p.filter((x) => x.slug !== slug)); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const approveAff = async (code) => {
    try { await api.post(`/admin/affiliates/${code}/approve`, { commission_rate: parseFloat(rates[code]) || 10 }); toast.success(`${code} approved`); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const payAff = async (code) => {
    try { const { data } = await api.post(`/admin/affiliates/${code}/pay`); toast.success(`Marked $${data.commission_paid.toFixed(2)} paid`); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-3 mb-8">
        <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">CONTROL CENTER</span>
      </div>
      <h1 className="font-display text-5xl tracking-tight mb-8">ADMIN PANEL</h1>

      <div className="flex flex-wrap gap-2 border-b border-ape-border mb-8">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`font-head uppercase tracking-widest text-sm px-5 py-3 border-b-2 -mb-px transition-colors duration-150 ${tab === t ? "border-ape-red text-ape-red" : "border-transparent text-ape-muted hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <div className="space-y-8" data-testid="admin-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: DollarSign, l: "Revenue (paid)", v: `$${revenueSeries.reduce((a, d) => a + d.revenue, 0).toFixed(2)}` },
              { icon: Package, l: "Orders", v: stats.orders },
              { icon: Users, l: "Customers", v: stats.customers },
              { icon: Boxes, l: "Products", v: stats.products },
            ].map((s, i) => (
              <div key={i} className="border border-ape-border bg-ape-surface p-6">
                <s.icon className="text-ape-red" size={28} />
                <div className="font-display text-4xl mt-4">{s.v}</div>
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mt-1">{s.l.toUpperCase()}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="font-head uppercase tracking-widest text-sm text-ape-muted">Sales Analytics</div>
            <div className="flex items-center gap-2" data-testid="chart-range">
              {[7, 14, 30, 90].map((r) => (
                <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                  className={`font-mono2 text-xs tracking-widest px-3 py-1.5 border transition-colors duration-150 ${range === r ? "bg-ape-red border-ape-red text-white" : "border-ape-border text-ape-muted hover:border-white hover:text-white"}`}>
                  {r}D
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-ape-border bg-ape-surface p-6" data-testid="chart-revenue">
              <div className="font-head uppercase tracking-widest text-sm mb-6">Revenue · Last {range} Days</div>
              {inRange.length === 0 ? (
                <div className="text-ape-muted text-sm h-[240px] flex items-center justify-center">No paid orders in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={revenueSeries} margin={{ left: -18, right: 6, top: 6 }}>
                    <defs>
                      <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E30613" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#E30613" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "#8a8a8a", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#2a2a2a" }} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tick={{ fill: "#8a8a8a", fontSize: 10 }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 0 }}
                      labelStyle={{ color: "#fff" }} formatter={(v) => [`$${v}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" stroke="#E30613" strokeWidth={2} fill="url(#revFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="border border-ape-border bg-ape-surface p-6" data-testid="chart-bestsellers">
              <div className="font-head uppercase tracking-widest text-sm mb-6">Best Sellers · Units Sold</div>
              {topSellers.length === 0 ? (
                <div className="text-ape-muted text-sm h-[240px] flex items-center justify-center">No sales data in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topSellers} layout="vertical" margin={{ left: 30, right: 12, top: 6 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
                    <XAxis type="number" tick={{ fill: "#8a8a8a", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#2a2a2a" }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: "#cfcfcf", fontSize: 10 }} tickLine={false} axisLine={false} width={90} />
                    <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 0 }}
                      labelStyle={{ color: "#fff" }} cursor={{ fill: "#ffffff08" }} formatter={(v) => [v, "Units"]} />
                    <Bar dataKey="qty" fill="#E30613" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="border border-ape-red/40 bg-ape-red/5 p-6 flex items-center justify-between flex-wrap gap-4" data-testid="reset-test-data-panel">
            <div>
              <div className="font-head uppercase tracking-widest text-sm text-ape-red">Reset Test Data</div>
              <p className="text-ape-muted text-sm mt-1 max-w-lg">Permanently deletes all orders, payment records and customer accounts, and zeroes affiliate commissions. Products and email signups are kept. Use this to clear test transactions before going live.</p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="reset-test-data-btn"
                  className="border-2 border-ape-red text-ape-red hover:bg-ape-red hover:text-white font-head uppercase tracking-widest text-xs px-5 py-3 flex items-center gap-2 transition-colors duration-150">
                  <Trash2 size={15} /> Reset Test Data
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-ape-surface border-ape-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display text-2xl">Clear all test data?</AlertDialogTitle>
                  <AlertDialogDescription className="text-ape-muted">
                    This deletes every order, payment record and customer account, and resets affiliate commissions to $0. Your products, prices and email signups stay. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-ape-border text-white">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={resetTestData} data-testid="reset-confirm-btn" className="bg-ape-red hover:bg-ape-redhover text-white">Yes, clear it all</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {tab === "Orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <div className="text-ape-muted">No orders yet.</div>}
          {orders.map((o) => {
            const paid = o.status === "paid";
            const s = o.shipping || {};
            const hasShipping = s.address || s.city || s.full_name;
            const open = openOrder === o.order_num;
            return (
              <div key={o.order_num} className="border border-ape-border bg-ape-surface p-5" data-testid={`admin-order-${o.order_num}`}>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
                  <div>
                    <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">ORDER</div>
                    <div className="font-mono2 text-sm">{o.order_num}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">CUSTOMER</div>
                    <div className="text-sm truncate">{o.email}</div>
                  </div>
                  <div>
                    <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">TOTAL</div>
                    <div className="font-mono2 font-bold text-ape-red">${o.total.toFixed(2)}</div>
                  </div>
                  <div className="sm:text-right">
                    <span className={`font-mono2 text-[10px] tracking-widest border px-2 py-1 uppercase ${paid ? "border-green-600 text-green-500" : "border-ape-red text-ape-red"}`} data-testid={`order-status-${o.order_num}`}>
                      {(o.status || "").replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
                <div className="text-sm text-ape-muted mt-3">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</div>
                {o.promo_code && <div className="text-xs text-ape-red mt-1 font-mono2">Promo: {o.promo_code} (−{o.discount_percent}%){o.affiliate_code ? ` · Affiliate: ${o.affiliate_code}` : ""}</div>}

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">TRACKING #</span>
                  <input value={tracking[o.order_num] ?? o.tracking_number ?? ""} onChange={(e) => setTracking({ ...tracking, [o.order_num]: e.target.value })}
                    placeholder="Add tracking number" data-testid={`tracking-input-${o.order_num}`}
                    className="flex-1 min-w-[180px] bg-transparent border-2 border-ape-border rounded-none px-3 py-2 text-white font-mono2 text-sm focus:border-ape-red focus:outline-none transition-colors duration-150" />
                  <button onClick={() => saveTracking(o.order_num)} data-testid={`save-tracking-${o.order_num}`}
                    className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-xs px-4 py-2.5 transition-colors duration-150">Save</button>
                  {o.tracking_number && <span className="font-mono2 text-[10px] text-green-500 tracking-widest">LIVE ON TRACKING PAGE</span>}
                </div>

                <button onClick={() => setOpenOrder(open ? null : o.order_num)} data-testid={`view-shipping-${o.order_num}`}
                  className="mt-3 inline-flex items-center gap-1.5 font-mono2 text-xs tracking-widest text-white hover:text-ape-red transition-colors duration-150">
                  <MapPin size={13} /> {open ? "HIDE" : "VIEW"} SHIPPING DETAILS <ChevronDown size={13} className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className="mt-3 border-t border-ape-border pt-3 font-mono2 text-sm" data-testid={`shipping-details-${o.order_num}`}>
                    {hasShipping ? (
                      <div className="space-y-1 text-white/90">
                        {s.full_name && <div><span className="text-ape-muted">Name: </span>{s.full_name}</div>}
                        {s.address && <div><span className="text-ape-muted">Address: </span>{s.address}</div>}
                        <div><span className="text-ape-muted">City/State/ZIP: </span>{[s.city, s.state, s.zip].filter(Boolean).join(", ") || "—"}</div>
                        {s.country && <div><span className="text-ape-muted">Country: </span>{s.country}</div>}
                        {o.notes && <div><span className="text-ape-muted">Notes: </span>{o.notes}</div>}
                      </div>
                    ) : (
                      <div className="text-ape-muted">No shipping address on file yet {paid ? "" : "(collected once payment completes)"}.</div>
                    )}
                    <div className="text-ape-muted text-xs mt-2">Placed {new Date(o.created_at).toLocaleString()}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "Emails" && (
        <div className="space-y-4" data-testid="admin-emails">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Mail className="text-ape-red" size={20} />
              <span className="font-head uppercase tracking-wide">10% Discount Signups</span>
              <span className="font-mono2 text-xs text-ape-muted border border-ape-border px-2 py-0.5" data-testid="emails-count">{leads.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={copyEmails} data-testid="copy-emails-btn"
                className="border-2 border-ape-border hover:border-ape-red text-white font-head uppercase tracking-widest text-xs px-4 py-2.5 flex items-center gap-2 transition-colors duration-150">
                <Copy size={14} /> Copy All
              </button>
              <button onClick={exportCsv} data-testid="export-csv-btn"
                className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-xs px-4 py-2.5 flex items-center gap-2 transition-colors duration-150">
                <Download size={14} /> Export CSV
              </button>
            </div>
          </div>

          {leads.length === 0 ? (
            <div className="text-ape-muted">No email signups yet.</div>
          ) : (
            <div className="border border-ape-border bg-ape-surface divide-y divide-ape-border">
              {leads.map((l, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`admin-lead-${i}`}>
                  <span className="text-sm truncate">{l.email}</span>
                  <span className="font-mono2 text-xs text-ape-muted shrink-0">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Products" && (
        <div className="space-y-3" data-testid="admin-products">
          {products.map((p) => {
            const edit = pricing[p.slug] || {};
            const inStock = edit.in_stock ?? p.in_stock ?? true;
            return (
              <div key={p.slug} className="border border-ape-border bg-ape-surface p-4 flex items-center gap-4 flex-wrap">
                <div className="h-2 w-10 shrink-0" style={{ backgroundColor: p.accent }} />
                <div className="flex-1 min-w-[160px]">
                  <div className="font-head uppercase tracking-wide">{p.name}</div>
                  <div className="font-mono2 text-xs text-ape-muted">{p.category} · {p.dosage}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-mono2 text-ape-muted">$</span>
                  <input type="number" step="0.01" value={edit.price ?? p.price}
                    onChange={(e) => setPricing({ ...pricing, [p.slug]: { ...edit, price: e.target.value } })}
                    data-testid={`price-input-${p.slug}`}
                    className="w-24 bg-transparent border-2 border-ape-border rounded-none px-2 py-2 text-white font-mono2 text-sm focus:border-ape-red focus:outline-none" />
                </div>
                <button onClick={() => setPricing({ ...pricing, [p.slug]: { ...edit, in_stock: !inStock } })}
                  data-testid={`stock-toggle-${p.slug}`}
                  className={`font-mono2 text-[10px] tracking-widest border px-3 py-2 uppercase transition-colors duration-150 ${inStock ? "border-green-600 text-green-500" : "border-ape-red text-ape-red"}`}>
                  {inStock ? "In Stock" : "Out of Stock"}
                </button>
                <button onClick={() => savePricing(p)} data-testid={`save-pricing-${p.slug}`}
                  className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-xs px-4 py-2.5 transition-colors duration-150">Save</button>
                <button onClick={() => delProduct(p.slug)} className="text-ape-muted hover:text-ape-red transition-colors duration-150" data-testid={`admin-del-${p.slug}`}><Trash2 size={18} /></button>
              </div>
            );
          })}
          <p className="text-ape-muted text-xs font-mono2 pt-2">Edit price & stock inline. Saving syncs the price to Stripe automatically.</p>
        </div>
      )}

      {tab === "Messages" && (
        <div className="space-y-3" data-testid="admin-messages">
          {messages.length === 0 && <div className="text-ape-muted">No messages yet.</div>}
          {messages.map((m, i) => (
            <div key={i} className="border border-ape-border bg-ape-surface p-5">
              <div className="flex items-center gap-2"><Mail className="text-ape-red" size={16} /><span className="font-head uppercase tracking-wide">{m.subject}</span></div>
              <div className="font-mono2 text-xs text-ape-muted mt-1">{m.name} · {m.email}</div>
              <p className="text-sm text-white/90 mt-2">{m.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "Affiliates" && (
        <div className="space-y-6" data-testid="admin-affiliates">
          {affStats && (
            <div className="border border-ape-red bg-ape-red/10 p-6 flex items-center justify-between flex-wrap gap-3" data-testid="total-commission-owed">
              <div className="flex items-center gap-3"><BadgeDollarSign className="text-ape-red" size={30} /><span className="font-head uppercase tracking-wide">Total Commissions Owed</span></div>
              <span className="font-display text-4xl text-ape-red">${affStats.total_commission_owed.toFixed(2)}</span>
            </div>
          )}

          {affiliates.length === 0 && <div className="text-ape-muted">No affiliate applications yet.</div>}

          {affiliates.map((a, i) => {
            const st = affStats?.affiliates?.find((x) => x.affiliate_code === a.affiliate_code);
            const approved = a.status === "approved";
            return (
              <div key={i} className="border border-ape-border bg-ape-surface p-5" data-testid={`admin-affiliate-${a.affiliate_code}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2"><span className="text-lg">🦍</span><span className="font-head uppercase tracking-wide">{a.full_name}</span> <span className="font-mono2 text-xs text-ape-red border border-ape-red px-2 py-0.5">{a.affiliate_code}</span></div>
                  <span className={`font-mono2 text-[10px] tracking-widest border px-2 py-1 uppercase ${approved ? "border-green-600 text-green-500" : "border-ape-border text-ape-muted"}`}>{a.status}</span>
                </div>
                <div className="font-mono2 text-xs text-ape-muted mt-2">{a.email}{a.social ? ` · ${a.social}` : ""}{a.location ? ` · ${a.location}` : ""}</div>
                {a.content_types?.length > 0 && <div className="text-xs text-white/80 mt-2">Content: {a.content_types.join(", ")}</div>}
                {a.about && <p className="text-sm text-white/90 mt-2">{a.about}</p>}

                {approved && st && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4 pt-4 border-t border-ape-border">
                    {[["Rate", `${st.commission_rate}%`], ["Sales", st.sales_count], ["Volume", `$${st.total_sales.toFixed(2)}`], ["Earned", `$${st.commission_total.toFixed(2)}`], ["Owed", `$${st.commission_owed.toFixed(2)}`]].map(([l, v]) => (
                      <div key={l}><div className="font-mono2 text-[10px] tracking-widest text-ape-muted">{l.toUpperCase()}</div><div className="font-display text-2xl">{v}</div></div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3 mt-4">
                  {!approved ? (
                    <>
                      <input type="number" placeholder="Rate %" value={rates[a.affiliate_code] || ""} onChange={(e) => setRates({ ...rates, [a.affiliate_code]: e.target.value })}
                        className="w-24 bg-transparent border-2 border-ape-border rounded-none px-3 py-2 text-white font-mono2 text-sm focus:border-ape-red focus:outline-none" data-testid={`rate-${a.affiliate_code}`} />
                      <button onClick={() => approveAff(a.affiliate_code)} data-testid={`approve-${a.affiliate_code}`}
                        className="bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-sm px-5 py-2.5 flex items-center gap-2 transition-colors duration-150"><Check size={15} /> Approve</button>
                    </>
                  ) : (
                    st && st.commission_owed > 0 && (
                      <button onClick={() => payAff(a.affiliate_code)} data-testid={`pay-${a.affiliate_code}`}
                        className="border-2 border-ape-red text-ape-red hover:bg-ape-red hover:text-white font-head uppercase tracking-widest text-sm px-5 py-2.5 transition-colors duration-150">Mark Commissions Paid</button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
